import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, BadgeCheck, Building2, CheckCircle2, FileCheck2, FileSignature, Fingerprint, Globe2, LockKeyhole, QrCode, Search, ShieldCheck, Sparkles, Users, Zap, } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
const Home = () => {
    const [keywords, setKeywords] = useState("");
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [userType, setUserType] = useState(null);
    const navigate = useNavigate();
    const { t, lang } = useLanguage();
    const tr = (key, fallback) => t?.ui?.home?.[key] || t?.[key] || fallback;
    const copy = t?.ui?.home || {};

    useEffect(() => {
        const updateSession = () => {
            const token = localStorage.getItem("token");
            let storedUser = null;
            try {
                storedUser = JSON.parse(localStorage.getItem("user") || "null");
            }
            catch {
                storedUser = null;
            }
            setIsLoggedIn(Boolean(token));
            setUserType(storedUser?.type || null);
        };
        updateSession();
        window.addEventListener("storage", updateSession);
        window.addEventListener("loginStateChanged", updateSession);
        return () => {
            window.removeEventListener("storage", updateSession);
            window.removeEventListener("loginStateChanged", updateSession);
        };
    }, []);
    const handleSearch = (event) => {
        event.preventDefault();
        const value = keywords.trim();
        if (!value)
            return;
        navigate(`/results?keywords=${encodeURIComponent(value)}`);
    };
    const trustItems = [
        {
            icon: Globe2,
            title: copy.publicVerification,
            text: copy.publicVerificationText,
            tone: "bg-cyan-50 text-cyan-700 ring-cyan-100",
        },
        {
            icon: Building2,
            title: copy.centralizedManagement,
            text: copy.centralizedManagementText,
            tone: "bg-blue-50 text-blue-700 ring-blue-100",
        },
        {
            icon: FileCheck2,
            title: copy.traceability,
            text: copy.traceabilityText,
            tone: "bg-emerald-50 text-emerald-700 ring-emerald-100",
        },
        {
            icon: LockKeyhole,
            title: copy.secureAccess,
            text: copy.secureAccessText,
            tone: "bg-violet-50 text-violet-700 ring-violet-100",
        },
    ];
    const isAdmin = isLoggedIn && userType === "Admin";
    const primaryCtaTarget = isAdmin
        ? "/dashboard"
        : isLoggedIn
            ? "/participants"
            : "/requests/create";
    const primaryCtaLabel = isAdmin
        ? copy.openDashboard
        : isLoggedIn
            ? t?.participants || copy.participants
            : copy.requestAccess;
    const finalCtaLabel = isAdmin
        ? copy.openDashboard
        : isLoggedIn
            ? t?.participants || copy.participants
            : copy.requestNow;
    const workflow = [
        {
            number: "01",
            icon: Building2,
            title: copy.step1Title,
            text: copy.step1Text,
        },
        {
            number: "02",
            icon: Users,
            title: copy.step2Title,
            text: copy.step2Text,
        },
        {
            number: "03",
            icon: QrCode,
            title: copy.step3Title,
            text: copy.step3Text,
        },
    ];
    return (<main className="min-h-screen overflow-hidden bg-slate-50 text-slate-900">
      <section className="relative isolate overflow-hidden bg-slate-950">
        <div className="absolute inset-0 -z-20 bg-[radial-gradient(circle_at_top_left,rgba(37,99,235,0.34),transparent_34%),radial-gradient(circle_at_82%_28%,rgba(6,182,212,0.22),transparent_28%),linear-gradient(135deg,#020617_0%,#0f172a_50%,#172554_100%)]"/>
        <div className="absolute inset-0 -z-10 opacity-30 [background-image:linear-gradient(rgba(148,163,184,0.12)_1px,transparent_1px),linear-gradient(90deg,rgba(148,163,184,0.12)_1px,transparent_1px)] [background-size:48px_48px]"/>
        <div className="absolute -left-24 top-24 h-72 w-72 rounded-full bg-blue-500/20 blur-3xl"/>
        <div className="absolute -right-20 bottom-0 h-80 w-80 rounded-full bg-cyan-400/10 blur-3xl"/>

        <div className="mx-auto grid min-h-[560px] max-w-7xl items-center gap-14 px-6 pb-16 pt-10 lg:grid-cols-[1.08fr_0.92fr] lg:px-8 lg:pb-20 lg:pt-14">
          <div className="relative z-10">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3.5 py-2 text-sm font-semibold text-blue-100 backdrop-blur">
              <Sparkles className="h-4 w-4 text-cyan-300"/>
              {copy.heroBadge}
            </div>

            <h1 className="mt-7 max-w-4xl text-4xl font-black leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-6xl">
              {t?.heroTitle || copy.heroTitle}
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-8 text-slate-300 sm:text-lg">
              {t?.heroSubtitle || copy.heroSubtitle}
            </p>

            <form onSubmit={handleSearch} className="mt-9 max-w-2xl rounded-2xl border border-white/15 bg-white/10 p-2 shadow-2xl shadow-blue-950/30 backdrop-blur-xl">
              <label htmlFor="certificate-search" className="mb-2 block px-3 pt-2 text-xs font-bold uppercase tracking-[0.18em] text-blue-200">
                {copy.searchLabel}
              </label>
              <div className="flex flex-col gap-2 sm:flex-row">
                <div className="flex min-w-0 flex-1 items-center rounded-xl bg-white px-4">
                  <Search className="h-5 w-5 shrink-0 text-blue-600"/>
                  <input id="certificate-search" type="text" value={keywords} onChange={(event) => setKeywords(event.target.value)} placeholder={t?.searchPlaceholder || copy.searchPlaceholder} className="min-w-0 flex-1 bg-transparent px-3 py-3.5 text-sm font-medium text-slate-900 outline-none placeholder:text-slate-400"/>
                </div>
                <button type="submit" disabled={!keywords.trim()} className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-6 py-3.5 text-sm font-bold text-white shadow-lg shadow-blue-950/20 transition hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-50">
                  {t?.search || copy.searchButton}
                  <ArrowRight className="h-4 w-4"/>
                </button>
              </div>
            </form>

            <div className="mt-7 flex flex-col gap-3 sm:flex-row">
              <Link to={primaryCtaTarget} className="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-5 py-3 text-sm font-bold text-slate-950 shadow-lg transition hover:-translate-y-0.5 hover:bg-blue-50">
                {primaryCtaLabel}
                <ArrowRight className="h-4 w-4"/>
              </Link>
              <Link to="/about" className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/20 bg-white/5 px-5 py-3 text-sm font-bold text-white transition hover:border-white/40 hover:bg-white/10">
                {copy.discover}
              </Link>
            </div>
          </div>

          <div className="relative mx-auto w-full max-w-xl lg:max-w-none">
            <style>{`
              @keyframes certilink-float {
                0%, 100% { transform: perspective(1400px) translateY(0px) rotateX(0deg) rotateY(0deg); }
                50% { transform: perspective(1400px) translateY(-8px) rotateX(1.2deg) rotateY(-3deg); }
              }
              @keyframes certilink-shine {
                0% { transform: translateX(-130%) rotate(20deg); }
                55%, 100% { transform: translateX(230%) rotate(20deg); }
              }
              .certilink-float { animation: certilink-float 7s ease-in-out infinite; transform-style: preserve-3d; }
              .certilink-shine { animation: certilink-shine 5.5s ease-in-out infinite; }
              @media (prefers-reduced-motion: reduce) {
                .certilink-float, .certilink-shine { animation: none; }
              }
            `}</style>
            <div className="absolute -inset-7 rounded-[2.5rem] bg-gradient-to-br from-blue-500/20 via-cyan-400/10 to-violet-500/20 blur-2xl"/>

            <div className="certilink-float relative overflow-hidden rounded-[2rem] border border-white/15 bg-white/[0.08] p-4 shadow-2xl shadow-black/30 backdrop-blur-2xl sm:p-5">
              <div className="mb-3 flex items-center justify-between px-1">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-blue-200">
                    CertiLink
                  </p>
                  <p className="mt-1 text-sm font-semibold text-white">
                    {copy.demoCertificate}
                  </p>
                </div>
                <div className="rounded-xl bg-emerald-400/15 p-2.5 text-emerald-300 ring-1 ring-emerald-300/20">
                  <BadgeCheck className="h-5 w-5"/>
                </div>
              </div>

              {/* Certificate mockup, horizontal format matching the real CertiLink certificate template */}
              <div className="relative overflow-hidden rounded-2xl bg-white p-4 shadow-xl sm:p-6">
                <div className="pointer-events-none absolute -left-14 -top-16 h-36 w-56 -rotate-[30deg] bg-gradient-to-br from-blue-800 via-blue-600 to-cyan-400"/>
                <div className="pointer-events-none absolute -left-20 -top-24 h-28 w-56 -rotate-[30deg] bg-gradient-to-br from-blue-400/60 via-blue-300/25 to-transparent"/>
                <div className="pointer-events-none absolute -right-14 -bottom-14 h-28 w-44 rotate-[30deg] bg-gradient-to-tl from-blue-800 via-blue-600 to-cyan-400 opacity-90"/>

                <div className="relative overflow-hidden rounded-xl border border-blue-100/80 p-4 sm:p-5">
                  <div className="pointer-events-none absolute inset-0 overflow-hidden">
                    <div className="certilink-shine absolute -top-1/2 left-0 h-[220%] w-1/4 bg-gradient-to-r from-transparent via-white/70 to-transparent"/>
                  </div>

                  <div className="relative flex items-start justify-between gap-3">
                    <div className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-[10px] font-bold text-emerald-700">
                      <CheckCircle2 className="h-3 w-3"/>
                      {copy.verified}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className="rounded-lg bg-blue-50 p-1.5 text-blue-700 ring-1 ring-blue-100">
                        <Fingerprint className="h-3.5 w-3.5"/>
                      </div>
                      <div className="text-right leading-tight">
                        <p className="text-[11px] font-black text-slate-950">CertiLink</p>
                        <p className="text-[7px] font-bold uppercase tracking-[0.14em] text-slate-400">
                          {tr("authenticityCertified", "Authenticity certified")}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="relative mt-4 text-center">
                    <h2 className="font-serif text-lg font-black uppercase tracking-[0.05em] text-slate-950 sm:text-xl">
                      {tr("certificateOfAchievement", "Certificate of Achievement")}
                    </h2>
                    <div className="mx-auto mt-2 h-px w-14 bg-gradient-to-r from-transparent via-blue-300 to-transparent"/>
                    <p className="mt-1.5 text-[11px] italic text-slate-500">{tr("digitalCredential", "Digital credential")}</p>
                  </div>

                  <div className="relative mt-4 grid grid-cols-3 gap-3 border-t border-slate-100 pt-3.5">
                    {[copy.identity, copy.issuer, copy.reference].map((label, index) => (<div key={label} className="flex flex-col items-start gap-1.5">
                        <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-blue-50 text-blue-700">
                          {index === 0 ? (<Users className="h-3 w-3"/>) : index === 1 ? (<Building2 className="h-3 w-3"/>) : (<FileSignature className="h-3 w-3"/>)}
                        </div>
                        <p className="text-[9px] font-semibold leading-tight text-slate-400">{label}</p>
                        <div className="h-1.5 w-full rounded-full bg-slate-200"/>
                      </div>))}
                  </div>

                  <div className="relative mt-4 flex items-end justify-between gap-4 border-t border-slate-100 pt-3.5">
                    <div className="grid h-14 w-14 grid-cols-7 gap-[2px] rounded-md border border-slate-200 bg-white p-1">
                      {Array.from({ length: 49 }).map((_, index) => {
            const active = index < 9 ||
                index % 6 === 0 ||
                index % 7 === 4 ||
                [17, 18, 24, 31, 38, 40, 46].includes(index);
            return (<span key={index} className={`rounded-[1px] ${active ? "bg-slate-900" : "bg-slate-100"}`}/>);
        })}
                    </div>

                    <div className="flex flex-col items-end gap-1">
                      <svg width="60" height="22" viewBox="0 0 60 22" fill="none" className="text-blue-600">
                        <path d="M1 16 C 8 3, 15 21, 22 8 S 34 1, 39 13 S 49 3, 59 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                      <div className="h-px w-12 bg-slate-300"/>
                      <div className="flex items-center gap-1 text-slate-400">
                        <ShieldCheck className="h-2.5 w-2.5"/>
                        <span className="text-[8px] font-bold uppercase tracking-wide">CertiLink</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-3 grid grid-cols-3 gap-3">
                {[ShieldCheck, Zap, QrCode].map((Icon, index) => (<div key={index} className="flex items-center justify-center rounded-xl border border-white/10 bg-white/[0.07] py-2.5 text-blue-100">
                    <Icon className="h-4 w-4"/>
                  </div>))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative z-10 mx-auto -mt-10 w-full max-w-7xl px-6 lg:px-8">
        <div className="grid overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl shadow-slate-200/50 sm:grid-cols-2 lg:grid-cols-4">
          {trustItems.map(({ icon: Icon, title, text, tone }, index) => (<div key={title} className={`p-5 sm:p-6 ${index > 0 ? "border-t border-slate-100 sm:border-t-0 sm:border-l" : ""}`}>
              <div className={`inline-flex rounded-xl p-2.5 ring-1 ${tone}`}>
                <Icon className="h-5 w-5"/>
              </div>
              <h2 className="mt-4 text-sm font-bold text-slate-950">{title}</h2>
              <p className="mt-1.5 text-xs leading-5 text-slate-500">{text}</p>
            </div>))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-24 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-xs font-black uppercase tracking-[0.2em] text-blue-600">
            {copy.workflowEyebrow}
          </p>
          <h2 className="mt-4 text-3xl font-black tracking-tight text-slate-950 sm:text-4xl">
            {copy.workflowTitle}
          </h2>
          <p className="mt-4 text-base leading-7 text-slate-600">{copy.workflowSubtitle}</p>
        </div>

        <div className="relative mt-14 grid gap-6 lg:grid-cols-3">
          <div className="absolute left-[16%] right-[16%] top-12 hidden h-px bg-gradient-to-r from-transparent via-blue-200 to-transparent lg:block"/>
          {workflow.map(({ number, icon: Icon, title, text }) => (<article key={number} className="group relative rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition hover:-translate-y-1 hover:border-blue-200 hover:shadow-xl">
              <div className="flex items-center justify-between">
                <div className="rounded-xl bg-blue-50 p-3 text-blue-700 ring-1 ring-blue-100">
                  <Icon className="h-6 w-6"/>
                </div>
                <span className="text-4xl font-black text-slate-100 transition group-hover:text-blue-100">
                  {number}
                </span>
              </div>
              <h3 className="mt-6 text-lg font-black text-slate-950">{title}</h3>
              <p className="mt-3 text-sm leading-6 text-slate-600">{text}</p>
            </article>))}
        </div>
      </section>

      <section className="border-y border-slate-200 bg-white">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-6 py-24 lg:grid-cols-2 lg:px-8">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.2em] text-cyan-700">
              {copy.benefitsEyebrow}
            </p>
            <h2 className="mt-4 text-3xl font-black tracking-tight text-slate-950 sm:text-4xl">
              {copy.benefitsTitle}
            </h2>
            <p className="mt-5 max-w-xl text-base leading-7 text-slate-600">
              {copy.benefitsText}
            </p>

            <div className="mt-8 space-y-4">
              {[copy.benefit1, copy.benefit2, copy.benefit3, copy.benefit4].map((benefit) => (<div key={benefit} className="flex items-start gap-3">
                    <div className="mt-0.5 rounded-full bg-emerald-50 p-1 text-emerald-600 ring-1 ring-emerald-100">
                      <CheckCircle2 className="h-4 w-4"/>
                    </div>
                    <p className="text-sm font-semibold leading-6 text-slate-700">{benefit}</p>
                  </div>))}
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="rounded-2xl border border-blue-100 bg-gradient-to-br from-blue-50 to-white p-6 sm:col-span-2">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.16em] text-blue-600">{tr("certilinkWorkspace", "CertiLink Workspace")}</p>
                  <h3 className="mt-2 text-xl font-black text-slate-950">
                    {copy.portalTitle}
                  </h3>
                </div>
                <div className="rounded-xl bg-blue-600 p-3 text-white shadow-lg shadow-blue-200">
                  <ShieldCheck className="h-6 w-6"/>
                </div>
              </div>
            </div>

            {[
            [Building2, copy.organization, copy.organizationText, "blue"],
            [Users, copy.participants, copy.participantsText, "cyan"],
            [BadgeCheck, copy.verifiers, copy.verifiersText, "emerald"],
        ].map(([Icon, title, text, tone], index) => {
            const color = {
                blue: "bg-blue-50 text-blue-700 ring-blue-100",
                cyan: "bg-cyan-50 text-cyan-700 ring-cyan-100",
                emerald: "bg-emerald-50 text-emerald-700 ring-emerald-100",
            }[tone];
            return (<article key={title} className={`rounded-2xl border border-slate-200 bg-white p-5 shadow-sm ${index === 2 ? "sm:col-span-2" : ""}`}>
                  <div className={`inline-flex rounded-xl p-2.5 ring-1 ${color}`}>
                    <Icon className="h-5 w-5"/>
                  </div>
                  <h3 className="mt-4 font-black text-slate-950">{title}</h3>
                  <p className="mt-2 text-sm leading-6 text-slate-600">{text}</p>
                </article>);
        })}
          </div>
        </div>
      </section>

      <section className="px-6 py-24 lg:px-8">
        <div className="relative mx-auto max-w-7xl overflow-hidden rounded-[2rem] bg-slate-950 px-6 py-12 text-center shadow-2xl sm:px-10 lg:py-16">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_10%_20%,rgba(37,99,235,0.32),transparent_30%),radial-gradient(circle_at_90%_70%,rgba(6,182,212,0.18),transparent_28%)]"/>
          <div className="relative mx-auto max-w-3xl">
            <div className="mx-auto inline-flex rounded-xl bg-white/10 p-3 text-cyan-300 ring-1 ring-white/10">
              <FileSignature className="h-6 w-6"/>
            </div>
            <h2 className="mt-5 text-3xl font-black tracking-tight text-white sm:text-4xl">
              {copy.ctaTitle}
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-sm leading-6 text-slate-300 sm:text-base">
              {copy.ctaText}
            </p>
            <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
              <Link to={primaryCtaTarget} className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-6 py-3.5 text-sm font-bold text-white transition hover:bg-blue-500">
                {finalCtaLabel}
                <ArrowRight className="h-4 w-4"/>
              </Link>
              <Link to="/about" className="inline-flex items-center justify-center rounded-xl border border-white/20 bg-white/5 px-6 py-3.5 text-sm font-bold text-white transition hover:bg-white/10">
                {copy.learnMore}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>);
};
export default Home;
